import React, { useState, useRef, useEffect } from 'react';
import { useT } from '../BuilderApp.jsx';
import WizardShell from '../WizardShell.jsx';
import { CheckIcon } from '../../components/ui/Icons.jsx';
import { PACKAGES, ADDONS, PKG_TEXT, addonTotalFor } from '../../data/packages.js';
import { pkgById } from '../../data/defaults.js';
import { walletLabel } from '../../data/packages.js';

function StepNextHint({ step }) {
  const t = useT();
  const msg = t[`next${step}`];
  if (!msg) return null;
  return (
    <div style={{display:'flex',alignItems:'center',gap:'.4rem',justifyContent:'center',marginTop:'.55rem',fontSize:'.72rem',color:'rgba(148,163,184,.55)',letterSpacing:'.01em',lineHeight:1.4}}>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{flexShrink:0,opacity:.55}}>
        <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

export default function Step6Package({ config, setField, next, back, lang, setLang }) {
  const t = useT();
  const [confirming, setConfirming] = useState(null);
  const timerRef = useRef(null);

  const pickPkg = (id) => {
    setField('package', id);
    setConfirming(id);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setConfirming(null), 600);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const selectedPkg = pkgById(config.package);
  const ac = PKG_TEXT[config.package] || '#3b82f6';
  const addons = config.addons || [];
  const useNOK = ['no', 'da', 'sv'].includes(lang);
  const addonTotal = addonTotalFor(addons, lang);
  const fmtAddon = n => useNOK ? `${n} kr${t.addons_mth}` : `€${n}${t.addons_mth}`;

  const addonMeta = {
    vipps:  { lbl: t.a_vipps_lbl,  sub: t.a_vipps_sub  },
    stripe: { lbl: t.a_stripe_lbl, sub: t.a_stripe_sub  },
    ivr:    { lbl: t.a_ivr_lbl,    sub: t.a_ivr_sub     },
    social: { lbl: t.a_social_lbl, sub: t.a_social_sub  },
  };

  const toggleAddon = id => {
    const next2 = addons.includes(id)
      ? addons.filter(x => x !== id)
      : [...addons, id];
    setField('addons', next2);
  };

  const always = [
    { icon:'🟠', label: walletLabel(lang), sub: t.incl_wallet_sub },
    { icon:'📞', label: t.incl_phone_lbl,  sub: t.incl_phone_sub  },
    { icon:'👥', label: t.incl_crm_lbl,    sub: t.incl_crm_sub    },
    { icon:'🌐', label: t.incl_site_lbl,   sub: t.incl_site_sub   },
    { icon:'🧾', label: t.incl_bill_lbl,   sub: t.incl_bill_sub   },
  ];

  const addonCategories = [
    { key:'pay', labelKey:'addons_cat_pay', ids:['vipps','stripe'] },
    { key:'svc', labelKey:'addons_cat_svc', ids:['ivr','social']   },
  ];

  const renderAddonRow = (a) => {
    const on = addons.includes(a.id);
    const meta = addonMeta[a.id] || {};
    const price = useNOK ? a.nok : a.eur;
    return (
      <div
        key={a.id}
        onClick={() => toggleAddon(a.id)}
        role="checkbox"
        aria-checked={on}
        tabIndex={0}
        onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && toggleAddon(a.id)}
        style={{
          background: on ? 'rgba(37,99,235,.1)' : 'rgba(255,255,255,.03)',
          border: `1.5px solid ${on ? 'rgba(59,130,246,.45)' : 'rgba(255,255,255,.08)'}`,
          borderRadius: 12, padding: '.7rem .85rem',
          display: 'flex', alignItems: 'center', gap: '.75rem',
          cursor: 'pointer',
          transition: 'background .18s, border-color .18s, box-shadow .18s',
          boxShadow: on ? '0 0 0 3px rgba(59,130,246,.1)' : 'none',
          userSelect: 'none',
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 9, flexShrink: 0,
          background: on ? 'rgba(59,130,246,.18)' : 'rgba(255,255,255,.06)',
          border: `1px solid ${on ? 'rgba(59,130,246,.3)' : 'rgba(255,255,255,.08)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.1rem', transition: 'background .18s',
        }}>{a.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '.8rem', fontWeight: 700, color: on ? '#e2e8f0' : 'var(--text)', lineHeight: 1.25, marginBottom: '.12rem' }}>{meta.lbl}</div>
          <div style={{ fontSize: '.68rem', color: 'var(--dim)', lineHeight: 1.4 }}>{meta.sub}</div>
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.3rem' }}>
          <span style={{
            fontSize: '.75rem', fontWeight: 800,
            color: on ? '#60a5fa' : 'var(--muted)',
            background: on ? 'rgba(59,130,246,.12)' : 'rgba(255,255,255,.04)',
            border: `1px solid ${on ? 'rgba(59,130,246,.25)' : 'rgba(255,255,255,.06)'}`,
            borderRadius: 6, padding: '.15rem .45rem',
            whiteSpace: 'nowrap',
          }}>{fmtAddon(price)}</span>
          <div style={{
            width: 34, height: 18, borderRadius: 9, flexShrink: 0,
            background: on ? '#2563eb' : 'rgba(255,255,255,.12)',
            border: `1.5px solid ${on ? '#1d4ed8' : 'rgba(255,255,255,.18)'}`,
            position: 'relative',
            transition: 'background .18s, border-color .18s',
          }}>
            <div style={{
              position: 'absolute', top: 1, left: on ? 16 : 1,
              width: 13, height: 13, borderRadius: '50%',
              background: on ? '#fff' : 'rgba(255,255,255,.5)',
              boxShadow: '0 1px 4px rgba(0,0,0,.25)',
              transition: 'left .18s, background .18s',
            }}/>
          </div>
        </div>
      </div>
    );
  };

  return (
    <WizardShell step={6} total={8} title={t.s5_title} subtitle={t.s5_sub} onBack={back} lang={lang} setLang={setLang}>

      {/* Always-included strip */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '.68rem', fontWeight: 800, letterSpacing: '.08em', color: 'var(--dim)', textTransform: 'uppercase', marginBottom: '.55rem' }}>
          {t.incl_all}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,220px),1fr))', gap: '.4rem' }}>
          {always.map(f => (
            <div key={f.label} style={{
              background: 'rgba(37,99,235,.07)',
              border: '1px solid rgba(37,99,235,.18)',
              borderRadius: 10, padding: '.55rem .7rem',
              display: 'flex', alignItems: 'center', gap: '.5rem',
            }}>
              <span style={{ fontSize: '1rem', flexShrink: 0 }}>{f.icon}</span>
              <div>
                <div style={{ fontSize: '.72rem', fontWeight: 700, color: '#e2e8f0', lineHeight: 1.2 }}>{f.label}</div>
                <div style={{ fontSize: '.62rem', color: 'var(--dim)', marginTop: '.08rem' }}>{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Package cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
        {PACKAGES.map((pkg, i) => {
          const pd = t.pkgs[i];
          const pkgAc = PKG_TEXT[pkg.id];
          const isSelected = config.package === pkg.id;
          const isConfirming = confirming === pkg.id;
          return (
            <div
              key={pkg.id}
              className={`pkg-card${isSelected ? ' selected' : ''}`}
              onClick={() => pickPkg(pkg.id)}
              style={{
                background: 'rgba(255,255,255,.02)',
                transform: isConfirming ? 'scale(0.985)' : 'none',
                transition: 'transform .15s, box-shadow .15s',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              {isConfirming && (
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: 'inherit',
                  background: 'rgba(37,99,235,.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 2, pointerEvents: 'none',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: '#2563eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 0 24px rgba(37,99,235,.5)',
                    animation: 'pkg-pop .25s ease-out',
                  }}>
                    <CheckIcon size={20} color="#fff"/>
                  </div>
                </div>
              )}
              {pkg.popular && (
                <div style={{ background: 'linear-gradient(90deg,#2563eb,#7c3aed)', textAlign: 'center', fontSize: '.7rem', fontWeight: 800, letterSpacing: '.07em', padding: '.3rem', color: '#fff' }}>
                  {t.s5_popular}
                </div>
              )}
              <div style={{ padding: '1.1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '.5rem' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.15rem' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: pkg.color }}/>
                      <span style={{ fontWeight: 800, fontSize: '.95rem' }}>{pkg.label}</span>
                    </div>
                    <div style={{ fontSize: '.74rem', color: 'var(--muted)', maxWidth: '60%', lineHeight: 1.4 }}>{pd.desc}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '.75rem' }}>
                    <div style={{ fontWeight: 800, fontSize: '1rem', color: isSelected ? pkgAc : 'var(--text)', lineHeight: 1.2 }}>{pd.price}</div>
                  </div>
                </div>
                {pd.campaign && (
                  <div style={{ background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.2)', borderRadius: 7, padding: '.35rem .6rem', fontSize: '.68rem', color: '#34d399', lineHeight: 1.4, marginBottom: '.5rem' }}>
                    {pd.campaign}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.28rem', marginTop: '.4rem' }}>
                  {pd.feats.slice(0, 4).map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.79rem', color: 'var(--muted)' }}>
                      <CheckIcon size={12} color={pkgAc}/>{f}
                    </div>
                  ))}
                  {pd.feats.length > 4 && (
                    <div style={{ fontSize: '.73rem', color: 'var(--dim)', paddingLeft: '1.1rem' }}>{t.s5_more(pd.feats.length - 4)}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add-on services */}
      <div style={{ marginTop: '1.5rem', marginBottom: '7rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '.55rem' }}>
          <div>
            <div style={{ fontSize: '.68rem', fontWeight: 800, letterSpacing: '.08em', color: 'var(--dim)', textTransform: 'uppercase' }}>{t.addons_h}</div>
            <div style={{ fontSize: '.68rem', color: 'rgba(148,163,184,.5)', marginTop: '.15rem' }}>{t.addons_sub}</div>
          </div>
          {addonTotal > 0 && (
            <div style={{ fontSize: '.78rem', fontWeight: 800, color: '#60a5fa', whiteSpace: 'nowrap', marginLeft: '.5rem' }}>+{fmtAddon(addonTotal)}</div>
          )}
        </div>

        {addonCategories.map(cat => (
          <div key={cat.key} style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.5rem' }}>
              <span style={{ fontSize: '.63rem', fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: 'rgba(148,163,184,.4)', whiteSpace: 'nowrap' }}>{t[cat.labelKey]}</span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,.06)' }}/>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
              {ADDONS.filter(a => cat.ids.includes(a.id)).map(renderAddonRow)}
            </div>
          </div>
        ))}

        <div style={{ marginTop: '.65rem', background: 'rgba(16,185,129,.07)', border: '1px solid rgba(16,185,129,.22)', borderRadius: 9, padding: '.5rem .75rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
          <span style={{ fontSize: '.85rem', flexShrink: 0 }}>🎁</span>
          <span style={{ fontSize: '.7rem', color: '#34d399', lineHeight: 1.45 }}>{t.addons_campaign.replace('🎁', '').trim()}</span>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(to top,var(--surface) 75%,transparent)',
        padding: '1rem 1rem 1.4rem',
        display: 'flex', flexDirection: 'column', gap: '.4rem',
        alignItems: 'stretch',
        zIndex: 50,
        maxWidth: 620, margin: '0 auto',
      }}>
        {addonTotal > 0 && (
          <div style={{ background: 'rgba(37,99,235,.08)', border: '1px solid rgba(37,99,235,.22)', borderRadius: 8, padding: '.45rem .75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.73rem' }}>
            <span style={{ color: 'var(--muted)' }}>{t.addons_total}</span>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ fontSize: '.68rem' }}>
                <span style={{ color: '#34d399', fontWeight: 700 }}>{t.addons_setup}</span>{' '}
                <span style={{ color: '#34d399', fontWeight: 800 }}>{t.addons_free}</span>
              </span>
              <span style={{ color: '#60a5fa', fontWeight: 800, fontSize: '.85rem' }}>{fmtAddon(addonTotal)}</span>
            </div>
          </div>
        )}
        <button
          className="btn btn-primary btn-lg btn-full"
          onClick={next}
          style={{
            boxShadow: '0 4px 24px rgba(37,99,235,.35)',
            background: `linear-gradient(90deg,${ac},${ac}cc)`,
          }}
        >
          {t.s5_btn(selectedPkg.label)}
        </button>
        <StepNextHint step={6}/>
      </div>
    </WizardShell>
  );
}
